import { z } from "zod";
import { UnixTimestamp } from "../types/branded";
import { JsonArraySchema, JsonObjectSchema } from "../types/json";

export const INTELLIO_ASSISTANT_MODULES = [
  "admissions",
  "financial-aid",
  "faculty",
  "settings",
  "students-families",
  "tuition",
  "attendance",
  "parent-portal",
  "general",
] as const;
export const IntellioAssistantModuleSchema = z.enum(INTELLIO_ASSISTANT_MODULES);
export type IntellioAssistantModule = z.infer<
  typeof IntellioAssistantModuleSchema
>;

export const IntellioContextEngineeringVariantSchema = z.enum([
  "baseline",
  "context_engineered",
]);
export type IntellioContextEngineeringVariant = z.infer<
  typeof IntellioContextEngineeringVariantSchema
>;

export const IntellioRoutingHintsSchema = z
  .object({
    experimentPathway: IntellioContextEngineeringVariantSchema.optional(),
    pathnameHint: z.string().trim().min(1).optional(),
    moduleHint: z.string().trim().min(1).optional(),
    intentHint: z.string().trim().min(1).optional(),
    activeCapabilityId: z.string().trim().min(1).optional(),
    activeScenarioKey: z.string().trim().min(1).optional(),
  })
  .catchall(z.unknown());
export type IntellioRoutingHints = z.infer<typeof IntellioRoutingHintsSchema>;

export const INTELLIO_ASSISTANT_PERSONA_LANES = [
  "staff-school",
  "parent-family",
] as const;
export const IntellioAssistantPersonaLaneSchema = z.enum(
  INTELLIO_ASSISTANT_PERSONA_LANES,
);
export type IntellioAssistantPersonaLane = z.infer<
  typeof IntellioAssistantPersonaLaneSchema
>;

export const IntellioOnBehalfWorkflowContextSchema = z
  .object({
    subjectType: z.string().trim().min(1),
    subjectId: z.string().trim().min(1),
    subjectLabel: z.string().trim().min(1).optional(),
    routeHint: z.string().trim().min(1).optional(),
  })
  .strict();
export type IntellioOnBehalfWorkflowContext = z.infer<
  typeof IntellioOnBehalfWorkflowContextSchema
>;

export const IntellioAuthorityContextSchema = z
  .object({
    actorId: z.string().trim().min(1),
    actorRole: z.string().trim().min(1),
    tenantId: z.string().trim().min(1),
    schoolId: z.string().trim().min(1).optional(),
    schoolScopeSource: z
      .enum(["profile_lookup", "route_context", "unknown"])
      .default("unknown"),
    pathname: z.string().trim().min(1).optional(),
    module: IntellioAssistantModuleSchema.optional(),
    persona: z.string().trim().min(1).optional(),
    onBehalfOf: IntellioOnBehalfWorkflowContextSchema.optional(),
  })
  .strict();
export type IntellioAuthorityContext = z.infer<
  typeof IntellioAuthorityContextSchema
>;

export const INTELLIO_ASSISTANT_ROUTE_FAMILIES = [
  "assistant-home",
  "admissions",
  "financial-aid",
  "settings",
  "students-families",
  "attendance",
  "academics",
  "substitutes",
  "faculty",
  "finance",
  "payroll",
  "camps",
  "donations",
  "tuition",
  "parent-portal",
  "unknown",
] as const;
export const IntellioAssistantRouteFamilySchema = z.enum(
  INTELLIO_ASSISTANT_ROUTE_FAMILIES,
);
export type IntellioAssistantRouteFamily = z.infer<
  typeof IntellioAssistantRouteFamilySchema
>;

export const INTELLIO_ASSISTANT_DATASOURCE_FAMILIES = [
  "admissions.applications",
  "admissions.documents",
  "admissions.timeline",
  "admissions.calendar",
  "financialAid.review",
  "settings.configuration",
  "settings.users",
  "studentsFamilies.records",
  "studentsFamilies.tuition",
  "studentsFamilies.documents",
  "studentsFamilies.notes",
  "parentPortal.records",
  "faculty.records",
  "attendance.records",
  "academics.records",
  "substitutes.records",
  "finance.records",
  "payroll.records",
  "tuition.records",
  "donations.records",
  "camps.records",
  "assistant.runtimePolicy",
  "assistant.feedbackArtifacts",
] as const;
export const IntellioAssistantDatasourceFamilySchema = z.enum(
  INTELLIO_ASSISTANT_DATASOURCE_FAMILIES,
);
export type IntellioAssistantDatasourceFamily = z.infer<
  typeof IntellioAssistantDatasourceFamilySchema
>;

export const INTELLIO_ASSISTANT_EXECUTION_ADAPTERS = [
  "domain-router-query",
  "domain-router-mutation",
  "postgres-direct-read",
  "dynamodb-direct-read",
  "artifact-read",
] as const;
export const IntellioAssistantExecutionAdapterSchema = z.enum(
  INTELLIO_ASSISTANT_EXECUTION_ADAPTERS,
);
export type IntellioAssistantExecutionAdapter = z.infer<
  typeof IntellioAssistantExecutionAdapterSchema
>;

export const IntellioAssistantSubjectScopeSchema = z
  .object({
    kind: z.enum([
      "application",
      "family",
      "student",
      "actor-self",
      "route-entity",
      "unknown",
    ]),
    ids: z.array(z.string().trim().min(1)).default([]),
    source: z
      .enum(["context", "route", "actor", "derived", "unknown"])
      .default("unknown"),
  })
  .strict();
export type IntellioAssistantSubjectScope = z.infer<
  typeof IntellioAssistantSubjectScopeSchema
>;

export const IntellioAssistantRouteFamilyAccessOverrideSchema = z
  .object({
    disabledCapabilityIds: z.array(z.string().trim().min(1)).default([]),
    disabledDatasourceFamilies: z
      .array(IntellioAssistantDatasourceFamilySchema)
      .default([]),
    disabledActionIds: z.array(z.string().trim().min(1)).default([]),
    allowDirectExecutorFamilies: z
      .array(IntellioAssistantDatasourceFamilySchema)
      .default([]),
  })
  .strict();
export type IntellioAssistantRouteFamilyAccessOverride = z.infer<
  typeof IntellioAssistantRouteFamilyAccessOverrideSchema
>;

export const IntellioAssistantRuntimeAccessControlSchema = z
  .object({
    disabledRouteFamilies: z
      .array(IntellioAssistantRouteFamilySchema)
      .default([]),
    disabledCapabilityIds: z.array(z.string().trim().min(1)).default([]),
    disabledDatasourceFamilies: z
      .array(IntellioAssistantDatasourceFamilySchema)
      .default([]),
    disabledActionIds: z.array(z.string().trim().min(1)).default([]),
    allowDirectExecutorFamilies: z
      .array(IntellioAssistantDatasourceFamilySchema)
      .default([]),
    routeFamilyOverrides: z
      .record(
        z.string().trim().min(1),
        IntellioAssistantRouteFamilyAccessOverrideSchema,
      )
      .default({}),
  })
  .strict();
export type IntellioAssistantRuntimeAccessControl = z.infer<
  typeof IntellioAssistantRuntimeAccessControlSchema
>;

export const IntellioAssistantDatasourcePlanStepSchema = z
  .object({
    datasourceFamily: IntellioAssistantDatasourceFamilySchema,
    executionAdapter: IntellioAssistantExecutionAdapterSchema,
  })
  .strict();
export type IntellioAssistantDatasourcePlanStep = z.infer<
  typeof IntellioAssistantDatasourcePlanStepSchema
>;

export const IntellioAssistantAccessScopeEnvelopeSchema =
  IntellioAuthorityContextSchema.extend({
    actorGroups: z.array(z.string().trim().min(1)).default([]),
    actorPersona: z.string().trim().min(1),
    assistantPersonaLane: IntellioAssistantPersonaLaneSchema,
    routeFamily: IntellioAssistantRouteFamilySchema,
    subjectScopes: z.array(IntellioAssistantSubjectScopeSchema).default([]),
    allowedDatasourceFamilies: z
      .array(IntellioAssistantDatasourceFamilySchema)
      .default([]),
    allowedActionIds: z.array(z.string().trim().min(1)).default([]),
    allowDirectExecutorFamilies: z
      .array(IntellioAssistantDatasourceFamilySchema)
      .default([]),
    runtimePolicyId: z.string().trim().min(1).nullable().optional(),
    runtimePolicyRevision: z.number().int().nonnegative().nullable().optional(),
  }).strict();
export type IntellioAssistantAccessScopeEnvelope = z.infer<
  typeof IntellioAssistantAccessScopeEnvelopeSchema
>;

export const IntellioAssistantResolvedDatasourcePlanSchema = z
  .object({
    capabilityId: z.string().trim().min(1).nullable().optional(),
    module: IntellioAssistantModuleSchema,
    routeFamily: IntellioAssistantRouteFamilySchema,
    requiredDatasourceFamilies: z.array(
      IntellioAssistantDatasourceFamilySchema,
    ),
    selectedDatasourceFamilies: z.array(
      IntellioAssistantDatasourceFamilySchema,
    ),
    executionPlan: z.array(IntellioAssistantDatasourcePlanStepSchema),
    allowed: z.boolean(),
    denialReason: z.string().trim().min(1).nullable().optional(),
  })
  .strict();
export type IntellioAssistantResolvedDatasourcePlan = z.infer<
  typeof IntellioAssistantResolvedDatasourcePlanSchema
>;

export const IntellioAssistantCapabilityAuthorizationDecisionSchema = z
  .object({
    capabilityId: z.string().trim().min(1),
    allowed: z.boolean(),
    denialReason: z.string().trim().min(1).nullable().optional(),
    datasourcePlan: IntellioAssistantResolvedDatasourcePlanSchema,
  })
  .strict();
export type IntellioAssistantCapabilityAuthorizationDecision = z.infer<
  typeof IntellioAssistantCapabilityAuthorizationDecisionSchema
>;

export const IntellioAssistantActionAuthorizationDecisionSchema = z
  .object({
    capabilityId: z.string().trim().min(1),
    actionId: z.string().trim().min(1).nullable().optional(),
    allowed: z.boolean(),
    denialReason: z.string().trim().min(1).nullable().optional(),
    datasourcePlan: IntellioAssistantResolvedDatasourcePlanSchema,
  })
  .strict();
export type IntellioAssistantActionAuthorizationDecision = z.infer<
  typeof IntellioAssistantActionAuthorizationDecisionSchema
>;

export const IntellioAssistantTaskAssetScopeSchema = z
  .object({
    assetOwnerActorId: z.string().trim().min(1),
    tenantId: z.string().trim().min(1),
    scopeMode: z.enum(["user_owned_assets"]),
    routeFamily: IntellioAssistantRouteFamilySchema,
    selectedDatasourceFamilies: z.array(
      IntellioAssistantDatasourceFamilySchema,
    ),
    executionPlan: z.array(IntellioAssistantDatasourcePlanStepSchema),
    subjectScopes: z.array(IntellioAssistantSubjectScopeSchema).default([]),
    runtimePolicyId: z.string().trim().min(1).nullable().optional(),
    runtimePolicyRevision: z.number().int().nonnegative().nullable().optional(),
  })
  .strict();
export type IntellioAssistantTaskAssetScope = z.infer<
  typeof IntellioAssistantTaskAssetScopeSchema
>;

export const AssistantReportFieldTypeSchema = z.enum([
  "text",
  "number",
  "date",
  "enum",
  "boolean",
]);
export type AssistantReportFieldType = z.infer<
  typeof AssistantReportFieldTypeSchema
>;

export const AssistantReportConditionSchema = z
  .object({
    id: z.string().trim().min(1),
    fieldId: z.string().trim().min(1),
    fieldLabel: z.string().trim().min(1),
    fieldType: AssistantReportFieldTypeSchema,
    operator: z.string().trim().min(1),
    value: z.string().default(""),
  })
  .strict();
export type AssistantReportCondition = z.infer<
  typeof AssistantReportConditionSchema
>;

export const AssistantReportSortSchema = z
  .object({
    fieldId: z.string().trim().min(1),
    fieldLabel: z.string().trim().min(1),
    direction: z.enum(["asc", "desc"]),
  })
  .strict();
export type AssistantReportSort = z.infer<typeof AssistantReportSortSchema>;

export const AssistantReportDateRangeSchema = z
  .object({
    preset: z.string().trim().min(1).default("Custom"),
    from: z.string().trim().nullable().optional(),
    to: z.string().trim().nullable().optional(),
  })
  .strict();
export type AssistantReportDateRange = z.infer<
  typeof AssistantReportDateRangeSchema
>;

export const AssistantReportDefinitionSchema = z
  .object({
    title: z.string().trim().min(1),
    pathname: z.string().trim().min(1).nullable().optional(),
    module: IntellioAssistantModuleSchema.optional(),
    routeFamily: IntellioAssistantRouteFamilySchema.optional(),
    templateId: z.string().trim().min(1).nullable().optional(),
    templateSource: z.enum(["gallery", "custom"]).nullable().optional(),
    selectedEntities: z.array(z.string().trim().min(1)).default([]),
    selectedFields: z.array(z.string().trim().min(1)).default([]),
    conditions: z.array(AssistantReportConditionSchema).default([]),
    dateRange: AssistantReportDateRangeSchema.default({ preset: "Custom" }),
    sorts: z.array(AssistantReportSortSchema).default([]),
  })
  .strict();
export type AssistantReportDefinition = z.infer<
  typeof AssistantReportDefinitionSchema
>;

export const AssistantReportExportFormatSchema = z.enum(["xlsx", "csv", "pdf"]);
export type AssistantReportExportFormat = z.infer<
  typeof AssistantReportExportFormatSchema
>;

export const AssistantReportTemplateVisibilitySchema = z.enum([
  "private",
  "shared",
]);
export type AssistantReportTemplateVisibility = z.infer<
  typeof AssistantReportTemplateVisibilitySchema
>;

export const IntellioAssistantReportTemplateSchema = z
  .object({
    id: z.string().trim().min(1),
    tenantId: z.string().trim().min(1),
    ownerActorId: z.string().trim().min(1),
    visibility: AssistantReportTemplateVisibilitySchema,
    title: z.string().trim().min(1),
    description: z.string().trim().nullable().optional(),
    version: z.number().int().positive().default(1),
    routeFamily: IntellioAssistantRouteFamilySchema,
    moduleKeys: z.array(IntellioAssistantModuleSchema).default([]),
    sourceTemplateId: z.string().trim().min(1).nullable().optional(),
    reportDefinition: AssistantReportDefinitionSchema,
    isArchived: z.boolean().default(false),
    lastRunAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    createdAtEpochMs: z.number().int().nonnegative(),
    updatedAtEpochMs: z.number().int().nonnegative(),
  })
  .strict();
export type IntellioAssistantReportTemplate = z.infer<
  typeof IntellioAssistantReportTemplateSchema
>;

export const IntellioAssistantReportScheduleSchema = z
  .object({
    id: z.string().trim().min(1),
    tenantId: z.string().trim().min(1),
    ownerActorId: z.string().trim().min(1),
    title: z.string().trim().min(1),
    enabled: z.boolean().default(true),
    reportTemplateId: z.string().trim().min(1).nullable().optional(),
    reportDefinition: AssistantReportDefinitionSchema,
    exportFormats: z
      .array(AssistantReportExportFormatSchema)
      .default(["xlsx", "csv", "pdf"]),
    scheduleExpression: z.string().trim().min(1),
    timezone: z.string().trim().min(1),
    nextRunAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    lastRunAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    lastRunId: z.string().trim().min(1).nullable().optional(),
    lastError: z.string().trim().min(1).nullable().optional(),
    createdAtEpochMs: z.number().int().nonnegative(),
    updatedAtEpochMs: z.number().int().nonnegative(),
  })
  .strict();
export type IntellioAssistantReportSchedule = z.infer<
  typeof IntellioAssistantReportScheduleSchema
>;

export const AssistantReportPreviewColumnSchema = z
  .object({
    fieldId: z.string().trim().min(1),
    label: z.string().trim().min(1),
    moduleLabel: z.string().trim().min(1),
    entityId: z.string().trim().min(1).nullable().optional(),
    authority: z
      .object({
        scope: z.string().trim().min(1),
        status: z.string().trim().min(1),
        source: z.string().trim().min(1).nullable().optional(),
        domainPackage: z.string().trim().min(1).nullable().optional(),
        domainType: z.string().trim().min(1).nullable().optional(),
        domainSchema: z.string().trim().min(1).nullable().optional(),
        operationId: z.string().trim().min(1),
        operationKind: z.string().trim().min(1),
        appSyncRouter: z.string().trim().min(1),
        appSyncField: z.string().trim().min(1),
        resultPath: z.string().trim().min(1),
        routeFamily: z.string().trim().min(1).nullable().optional(),
        datasourceFamily: z.string().trim().min(1).nullable().optional(),
        joinKey: z.string().trim().min(1).nullable().optional(),
        permission: z.string().trim().min(1).nullable().optional(),
      })
      .strict()
      .nullable()
      .optional(),
  })
  .strict();
export type AssistantReportPreviewColumn = z.infer<
  typeof AssistantReportPreviewColumnSchema
>;

export const AssistantReportPreviewRowSchema = z
  .object({
    id: z.string().trim().min(1),
    cells: z.record(
      z.string(),
      z.union([z.string(), z.number(), z.boolean(), z.null()]),
    ),
  })
  .strict();
export type AssistantReportPreviewRow = z.infer<
  typeof AssistantReportPreviewRowSchema
>;

export const AssistantReportPreviewDiagnosticSchema = z
  .object({
    code: z.string().trim().min(1),
    severity: z.enum(["info", "warning", "error"]).default("info"),
    message: z.string().trim().min(1),
    rowCount: z.number().int().nonnegative().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type AssistantReportPreviewDiagnostic = z.infer<
  typeof AssistantReportPreviewDiagnosticSchema
>;

export const AssistantReportPreviewSchema = z
  .object({
    definition: AssistantReportDefinitionSchema,
    columns: z.array(AssistantReportPreviewColumnSchema).default([]),
    rows: z.array(AssistantReportPreviewRowSchema).default([]),
    totalRowCount: z.number().int().nonnegative().default(0),
    joins: z.array(z.string().trim().min(1)).default([]),
    blockedEntityIds: z.array(z.string().trim().min(1)).default([]),
    blockedTemplateIds: z.array(z.string().trim().min(1)).default([]),
    canRun: z.boolean().default(false),
    warnings: z.array(z.string().trim().min(1)).default([]),
    diagnostics: z.array(AssistantReportPreviewDiagnosticSchema).default([]),
    datasourcePlan:
      IntellioAssistantResolvedDatasourcePlanSchema.nullable().optional(),
  })
  .strict();
export type AssistantReportPreview = z.infer<
  typeof AssistantReportPreviewSchema
>;

export const IntellioOperationalMatrixPresetSchema = z.enum([
  "conservative",
  "balanced",
  "aggressive",
]);
export type IntellioOperationalMatrixPreset = z.infer<
  typeof IntellioOperationalMatrixPresetSchema
>;

export const IntellioOperationalMatrixSchema = z
  .object({
    preset: IntellioOperationalMatrixPresetSchema.default("conservative"),
    allowedActionCategories: z.array(z.string().trim().min(1)).default([]),
    blockedActionCategories: z.array(z.string().trim().min(1)).default([]),
    sessionApprovedCategories: z.array(z.string().trim().min(1)).default([]),
    overrideKeys: z.array(z.string().trim().min(1)).default([]),
    lowConfidenceMode: z
      .enum(["navigation_only", "answer_only"])
      .default("navigation_only"),
  })
  .strict();
export type IntellioOperationalMatrix = z.infer<
  typeof IntellioOperationalMatrixSchema
>;

export const IntellioSensitivePromptDispositionSchema = z
  .object({
    level: z.enum(["standard", "sensitive", "restricted"]).default("standard"),
    treatment: z
      .enum(["normal", "summary_only", "navigation_only"])
      .default("normal"),
    note: z.string().trim().min(1).optional(),
  })
  .strict();
export type IntellioSensitivePromptDisposition = z.infer<
  typeof IntellioSensitivePromptDispositionSchema
>;

export const IntellioInterventionPrioritySchema = z.enum([
  "low",
  "medium",
  "high",
]);
export type IntellioInterventionPriority = z.infer<
  typeof IntellioInterventionPrioritySchema
>;

export const IntellioProactiveChannelSchema = z.enum(["in_app"]);
export type IntellioProactiveChannel = z.infer<
  typeof IntellioProactiveChannelSchema
>;

export const IntellioProactiveNotificationDraftSchema = z
  .object({
    title: z.string().trim().min(1),
    body: z.string().trim().min(1),
    priority: IntellioInterventionPrioritySchema.default("medium"),
    channels: z.array(IntellioProactiveChannelSchema).default(["in_app"]),
    module: IntellioAssistantModuleSchema.default("general"),
    route: z.string().trim().min(1),
    sourceRef: z.string().trim().min(1),
  })
  .strict();
export type IntellioProactiveNotificationDraft = z.infer<
  typeof IntellioProactiveNotificationDraftSchema
>;

export const AssistantPromptActionSchema = z.object({
  id: z.string().optional(),
  label: z.string(),
  kind: z.enum(["followup", "navigate", "execute", "draft"]).optional(),
  capabilityId: z.string().nullable().optional(),
  prompt: z.string().nullable().optional(),
  targetRoute: z.string().nullable().optional(),
  entityRef: z
    .object({
      kind: z.string().trim().min(1),
      id: z.string().trim().min(1),
      label: z.string().trim().min(1).nullable().optional(),
      module: IntellioAssistantModuleSchema.optional(),
    })
    .nullable()
    .optional(),
  requiresConfirmation: z.boolean().optional(),
  approvalCategory: z.string().trim().min(1).nullable().optional(),
  actionCategory: z.string().trim().min(1).nullable().optional(),
  mutationOperation: z.string().trim().min(1).nullable().optional(),
  missingInputs: z.array(z.string().trim().min(1)).optional(),
  confidenceMode: z.enum(["full", "navigation_only"]).optional(),
});
export type AssistantPromptAction = z.infer<typeof AssistantPromptActionSchema>;

export const AssistantPromptBindingSchema = z.object({
  module: IntellioAssistantModuleSchema.optional(),
  activeCapabilityId: z.string().trim().min(1).optional(),
  activeScenarioKey: z.string().trim().min(1).optional(),
  activeCapabilityMethod: z.string().trim().min(1).optional(),
});
export type AssistantPromptBinding = z.infer<
  typeof AssistantPromptBindingSchema
>;

export const AssistantPromptPayloadSchema = z.object({
  promptId: z.string(),
  kind: z.string(),
  seedQuestion: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  traceId: z.string().nullable().optional(),
  assistantEpisodeId: z.string().nullable().optional(),
  autoOpen: z.boolean().optional(),
  expiresAt: z.number().int().nonnegative().nullable().optional(),
  promptBinding: AssistantPromptBindingSchema.nullable().optional(),
  suggestedActions: z.array(AssistantPromptActionSchema).default([]),
});
export type AssistantPromptPayload = z.infer<
  typeof AssistantPromptPayloadSchema
>;

export const IntellioAssistantConversationSessionStatusSchema = z.enum([
  "open",
  "closed",
]);
export type IntellioAssistantConversationSessionStatus = z.infer<
  typeof IntellioAssistantConversationSessionStatusSchema
>;

export const IntellioAssistantConversationSourceAuthoritySchema = z.enum([
  "persisted",
  "legacy-import",
]);
export type IntellioAssistantConversationSourceAuthority = z.infer<
  typeof IntellioAssistantConversationSourceAuthoritySchema
>;

export const IntellioAssistantConversationMessageSourceSchema = z.enum([
  "client",
  "task-runtime",
  "system",
  "legacy-import",
]);
export type IntellioAssistantConversationMessageSource = z.infer<
  typeof IntellioAssistantConversationMessageSourceSchema
>;

export const IntellioAssistantConversationOriginRefSchema = z
  .object({
    sessionId: z.string().trim().min(1),
    threadId: z.string().trim().min(1).nullable().optional(),
    userMessageId: z.string().trim().min(1).nullable().optional(),
    assistantMessageId: z.string().trim().min(1).nullable().optional(),
  })
  .strict();
export type IntellioAssistantConversationOriginRef = z.infer<
  typeof IntellioAssistantConversationOriginRefSchema
>;

export const IntellioAssistantConversationSessionSchema = z
  .object({
    id: z.string(),
    actorId: z.string(),
    actorRole: z.string(),
    tenantId: z.string(),
    tenantScopeStatus: z.enum(["scoped", "legacy_placeholder"]).optional(),
    status: IntellioAssistantConversationSessionStatusSchema,
    module: IntellioAssistantModuleSchema,
    route: z.string().trim().min(1),
    persona: z.string().trim().min(1).nullable().optional(),
    title: z.string().trim().min(1).nullable().optional(),
    routeLabel: z.string().trim().min(1).nullable().optional(),
    firstUserMessageId: z.string().trim().min(1).nullable().optional(),
    firstUserPreview: z.string().trim().min(1).nullable().optional(),
    lastMessageId: z.string().trim().min(1).nullable().optional(),
    lastMessagePreview: z.string().trim().min(1).nullable().optional(),
    lastAssistantMessageId: z.string().trim().min(1).nullable().optional(),
    lastAssistantPreview: z.string().trim().min(1).nullable().optional(),
    messageCount: z.number().int().nonnegative(),
    sourceAuthority:
      IntellioAssistantConversationSourceAuthoritySchema.default("persisted"),
    startedAtEpochMs: z.number().int().nonnegative(),
    updatedAtEpochMs: z.number().int().nonnegative(),
    closedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  })
  .strict();
export type IntellioAssistantConversationSession = z.infer<
  typeof IntellioAssistantConversationSessionSchema
>;

export const IntellioAssistantConversationMessageSchema = z
  .object({
    sessionId: z.string().trim().min(1),
    messageId: z.string().trim().min(1),
    actorId: z.string().trim().min(1),
    actorRole: z.string().trim().min(1),
    tenantId: z.string().trim().min(1),
    tenantScopeStatus: z.enum(["scoped", "legacy_placeholder"]).optional(),
    sequence: z.number().int().nonnegative(),
    role: z.string().trim().min(1),
    content: z.string(),
    citationsJson: z.string().nullable().optional(),
    actionsJson: z.string().nullable().optional(),
    metadataJson: z.string().nullable().optional(),
    source: IntellioAssistantConversationMessageSourceSchema.default("client"),
    threadId: z.string().trim().min(1).nullable().optional(),
    runId: z.string().trim().min(1).nullable().optional(),
    workItemId: z.string().trim().min(1).nullable().optional(),
    createdAtEpochMs: z.number().int().nonnegative(),
  })
  .strict();
export type IntellioAssistantConversationMessage = z.infer<
  typeof IntellioAssistantConversationMessageSchema
>;

export const IntellioAssistantWorkStateSchema = z.enum([
  "queued",
  "opened",
  "snoozed",
  "dismissed",
  "completed",
  "failed",
  "cancelled",
  "expired",
]);
export type IntellioAssistantWorkState = z.infer<
  typeof IntellioAssistantWorkStateSchema
>;

export const IntellioAssistantRunStateSchema = z.enum([
  "queued",
  "started",
  "streaming",
  "waiting_for_confirmation",
  "completed",
  "failed",
  "cancelled",
]);
export type IntellioAssistantRunState = z.infer<
  typeof IntellioAssistantRunStateSchema
>;

export const IntellioAssistantUrgencySchema = z.enum([
  "low",
  "normal",
  "high",
  "critical",
]);
export type IntellioAssistantUrgency = z.infer<
  typeof IntellioAssistantUrgencySchema
>;

export const IntellioAssistantTaskClassSchema = z.enum([
  "artifact_generate",
  "compute_report",
  "draft_then_approve",
  "external_side_effect",
]);
export type IntellioAssistantTaskClass = z.infer<
  typeof IntellioAssistantTaskClassSchema
>;

export const IntellioAssistantTaskPhaseSchema = z.enum([
  "queued",
  "planning",
  "collecting_data",
  "validating_scope",
  "generating",
  "packaging_artifact",
  "waiting_for_confirmation",
  "sending",
  "completed",
  "failed",
  "cancelled",
]);
export type IntellioAssistantTaskPhase = z.infer<
  typeof IntellioAssistantTaskPhaseSchema
>;

export const ASSISTANT_WORKFLOW_TEMPLATE_IDS = [
  "admissions.dataset_to_spreadsheet_delivery",
  "admissions.dataset_to_chart_delivery",
  "admissions.new_application_threshold_notify",
] as const;
export const AssistantWorkflowTemplateIdSchema = z.enum(
  ASSISTANT_WORKFLOW_TEMPLATE_IDS,
);
export type AssistantWorkflowTemplateId = z.infer<
  typeof AssistantWorkflowTemplateIdSchema
>;

export const AssistantWorkflowApprovalStatusSchema = z.enum([
  "draft",
  "pending_approval",
  "approved",
  "rejected",
]);
export type AssistantWorkflowApprovalStatus = z.infer<
  typeof AssistantWorkflowApprovalStatusSchema
>;

export const AssistantWorkflowReviewDecisionSchema = z.enum([
  "approved",
  "rejected",
]);
export type AssistantWorkflowReviewDecision = z.infer<
  typeof AssistantWorkflowReviewDecisionSchema
>;

export const AssistantWorkflowTriggerKindSchema = z.enum([
  "manual",
  "schedule",
  "admissions_threshold",
]);
export type AssistantWorkflowTriggerKind = z.infer<
  typeof AssistantWorkflowTriggerKindSchema
>;

export const AssistantWorkflowConnectorKindSchema = z.enum([
  "email",
  "google_sheet",
  "google_drive",
]);
export type AssistantWorkflowConnectorKind = z.infer<
  typeof AssistantWorkflowConnectorKindSchema
>;

export const AssistantWorkflowNodeKindSchema = z.enum([
  "manual",
  "schedule",
  "admissions_threshold",
  "dataset",
  "filter",
  "select",
  "group",
  "aggregate",
  "branch",
  "spreadsheet",
  "chart",
  "email",
  "google_sheet",
  "google_drive",
]);
export type AssistantWorkflowNodeKind = z.infer<
  typeof AssistantWorkflowNodeKindSchema
>;

export const AssistantWorkflowExecutionStatusSchema = z.enum([
  "queued",
  "running",
  "waiting_for_confirmation",
  "ready",
  "failed",
  "cancelled",
  "blocked",
]);
export type AssistantWorkflowExecutionStatus = z.infer<
  typeof AssistantWorkflowExecutionStatusSchema
>;

export const AssistantWorkflowGraphNodeSchema = z
  .object({
    id: z.string().trim().min(1),
    kind: AssistantWorkflowNodeKindSchema,
    label: z.string().trim().min(1),
    position: z
      .object({
        x: z.number(),
        y: z.number(),
      })
      .strict()
      .optional(),
    config: JsonObjectSchema.optional(),
  })
  .strict();
export type AssistantWorkflowGraphNode = z.infer<
  typeof AssistantWorkflowGraphNodeSchema
>;

export const AssistantWorkflowGraphEdgeSchema = z
  .object({
    id: z.string().trim().min(1),
    source: z.string().trim().min(1),
    target: z.string().trim().min(1),
    label: z.string().trim().min(1).nullable().optional(),
    condition: z.string().trim().min(1).nullable().optional(),
  })
  .strict();
export type AssistantWorkflowGraphEdge = z.infer<
  typeof AssistantWorkflowGraphEdgeSchema
>;

export const AssistantWorkflowGraphSpecSchema = z
  .object({
    version: z.string().trim().min(1).default("v1"),
    nodes: z.array(AssistantWorkflowGraphNodeSchema).min(1),
    edges: z.array(AssistantWorkflowGraphEdgeSchema).default([]),
  })
  .strict();
export type AssistantWorkflowGraphSpec = z.infer<
  typeof AssistantWorkflowGraphSpecSchema
>;

export const AssistantWorkflowDatasourceSelectionPlanSchema = z
  .object({
    module: z.literal("admissions"),
    routeScope: z.string().trim().min(1),
    selectorSummary: z.string().trim().min(1).nullable().optional(),
    authorizedSources: z.array(z.string().trim().min(1)).default([]),
    filters: JsonArraySchema.default([]),
    projections: z.array(z.string().trim().min(1)).default([]),
    groupBy: z.array(z.string().trim().min(1)).default([]),
    aggregates: z.array(z.string().trim().min(1)).default([]),
  })
  .strict();
export type AssistantWorkflowDatasourceSelectionPlan = z.infer<
  typeof AssistantWorkflowDatasourceSelectionPlanSchema
>;

export const AssistantWorkflowConnectorBindingSchema = z
  .object({
    kind: AssistantWorkflowConnectorKindSchema,
    bindingId: z.string().trim().min(1),
    label: z.string().trim().min(1).nullable().optional(),
    recipientEmail: z.string().trim().email().nullable().optional(),
    destinationFolderId: z.string().trim().min(1).nullable().optional(),
    destinationFileId: z.string().trim().min(1).nullable().optional(),
    oauthSubject: z.string().trim().min(1).nullable().optional(),
    requiredScopes: z.array(z.string().trim().min(1)).default([]),
    config: JsonObjectSchema.optional(),
  })
  .strict();
export type AssistantWorkflowConnectorBinding = z.infer<
  typeof AssistantWorkflowConnectorBindingSchema
>;

export const AssistantWorkflowTriggerConfigSchema = z.discriminatedUnion(
  "kind",
  [
    z
      .object({
        kind: z.literal("manual"),
      })
      .strict(),
    z
      .object({
        kind: z.literal("schedule"),
        scheduleExpression: z.string().trim().min(1),
        timezone: z.string().trim().min(1),
        scheduleDescription: z.string().trim().min(1).nullable().optional(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("admissions_threshold"),
        metric: z.enum(["new_applications"]),
        threshold: z.number().int().positive(),
        window: z
          .enum(["rolling_hour", "rolling_day", "rolling_week"])
          .default("rolling_day"),
      })
      .strict(),
  ],
);
export type AssistantWorkflowTriggerConfig = z.infer<
  typeof AssistantWorkflowTriggerConfigSchema
>;

export const AssistantWorkflowApprovalSchema = z
  .object({
    id: z.string().trim().min(1),
    workflowDefinitionId: z.string().trim().min(1),
    status: AssistantWorkflowApprovalStatusSchema,
    reviewerActorId: z.string().trim().min(1).nullable().optional(),
    reviewerActorRole: z.string().trim().min(1).nullable().optional(),
    comment: z.string().trim().min(1).nullable().optional(),
    autonomousDelivery: z.boolean().default(false),
    reviewedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    createdAtEpochMs: z.number().int().nonnegative(),
    updatedAtEpochMs: z.number().int().nonnegative(),
  })
  .strict();
export type AssistantWorkflowApproval = z.infer<
  typeof AssistantWorkflowApprovalSchema
>;

export const AssistantWorkflowTemplateSchema = z
  .object({
    id: AssistantWorkflowTemplateIdSchema,
    title: z.string().trim().min(1),
    summary: z.string().trim().min(1),
    module: z.literal("admissions"),
    routeScope: z.string().trim().min(1),
    curatorOwned: z.boolean().default(true),
    supportedTriggers: z.array(AssistantWorkflowTriggerKindSchema).default([]),
    supportedConnectors: z
      .array(AssistantWorkflowConnectorKindSchema)
      .default([]),
    nodeCatalog: z.array(AssistantWorkflowNodeKindSchema).default([]),
    defaultGraphSpec: AssistantWorkflowGraphSpecSchema,
    mermaidSpec: z.string().trim().min(1).nullable().optional(),
    requiresModeration: z.boolean().default(true),
    allowsAutonomousDelivery: z.boolean().default(false),
    createdAtEpochMs: z.number().int().nonnegative().optional(),
    updatedAtEpochMs: z.number().int().nonnegative().optional(),
  })
  .strict();
export type AssistantWorkflowTemplate = z.infer<
  typeof AssistantWorkflowTemplateSchema
>;

export const AssistantWorkflowDefinitionSchema = z
  .object({
    id: z.string().trim().min(1),
    templateId: AssistantWorkflowTemplateIdSchema,
    title: z.string().trim().min(1),
    description: z.string().trim().min(1).nullable().optional(),
    creatorActorId: z.string().trim().min(1),
    creatorActorRole: z.string().trim().min(1).nullable().optional(),
    tenantId: z.string().trim().min(1),
    tenantScopeStatus: z
      .lazy(() => IntellioAssistantTenantScopeStatusSchema)
      .optional(),
    module: z.literal("admissions"),
    routeScope: z.string().trim().min(1),
    graphSpec: AssistantWorkflowGraphSpecSchema,
    mermaidSpec: z.string().trim().min(1),
    datasourceSelectionPlan: AssistantWorkflowDatasourceSelectionPlanSchema,
    connectorBindings: z
      .array(AssistantWorkflowConnectorBindingSchema)
      .default([]),
    triggerConfig: AssistantWorkflowTriggerConfigSchema,
    approvalStatus: AssistantWorkflowApprovalStatusSchema,
    autonomousDelivery: z.boolean().default(false),
    timezone: z.string().trim().min(1),
    executionSnapshotHash: z.string().trim().min(1),
    compiledAslJson: z.string().trim().min(1),
    compiledExecutionPackage: JsonObjectSchema,
    requiredOauthScopes: z.array(z.string().trim().min(1)).default([]),
    latestApproval: AssistantWorkflowApprovalSchema.nullable().optional(),
    lastSubmittedAtEpochMs: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .optional(),
    lastReviewedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    lastCompiledAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    createdAtEpochMs: z.number().int().nonnegative(),
    updatedAtEpochMs: z.number().int().nonnegative(),
  })
  .strict();
export type AssistantWorkflowDefinition = z.infer<
  typeof AssistantWorkflowDefinitionSchema
>;

export const AssistantWorkflowExecutionSchema = z
  .object({
    id: z.string().trim().min(1),
    workflowDefinitionId: z.string().trim().min(1),
    creatorActorId: z.string().trim().min(1),
    tenantId: z.string().trim().min(1),
    module: z.literal("admissions"),
    route: z.string().trim().min(1),
    runId: z.string().trim().min(1).nullable().optional(),
    workflowKey: z.string().trim().min(1).nullable().optional(),
    triggerKind: AssistantWorkflowTriggerKindSchema,
    triggerContext: JsonObjectSchema.nullable().optional(),
    status: AssistantWorkflowExecutionStatusSchema,
    approvalRequired: z.boolean().default(false),
    executionSnapshotHash: z.string().trim().min(1),
    stateMachineExecutionArn: z.string().trim().min(1).nullable().optional(),
    blockedReason: z.string().trim().min(1).nullable().optional(),
    resultSummary: z.string().trim().min(1).nullable().optional(),
    errorMessage: z.string().trim().min(1).nullable().optional(),
    artifactRefs: z
      .array(z.lazy(() => IntellioAssistantArtifactRefSchema))
      .default([]),
    createdAtEpochMs: z.number().int().nonnegative(),
    startedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    completedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    updatedAtEpochMs: z.number().int().nonnegative(),
  })
  .strict();
export type AssistantWorkflowExecution = z.infer<
  typeof AssistantWorkflowExecutionSchema
>;

export const AssistantWorkflowTriggerStateSchema = z
  .object({
    id: z.string().trim().min(1),
    workflowDefinitionId: z.string().trim().min(1),
    triggerKind: AssistantWorkflowTriggerKindSchema,
    metricKey: z.string().trim().min(1),
    counter: z.number().int().nonnegative(),
    threshold: z.number().int().positive().nullable().optional(),
    windowStartedAtEpochMs: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .optional(),
    lastObservedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
    lastTriggeredAtEpochMs: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .optional(),
    metadata: JsonObjectSchema.nullable().optional(),
    createdAtEpochMs: z.number().int().nonnegative(),
    updatedAtEpochMs: z.number().int().nonnegative(),
  })
  .strict();
export type AssistantWorkflowTriggerState = z.infer<
  typeof AssistantWorkflowTriggerStateSchema
>;

export const IntellioAssistantArtifactTypeSchema = z.enum([
  "spreadsheet",
  "spreadsheet-workbook",
  "chart",
  "visualization3d",
  "diagram",
  "html-page",
  "invoice",
  "report",
]);
export type IntellioAssistantArtifactType = z.infer<
  typeof IntellioAssistantArtifactTypeSchema
>;

export const IntellioAssistantArtifactActionKindSchema = z.enum([
  "download",
  "open_workspace",
  "export_google_sheets",
  "open_external",
  "hydrate_history",
]);
export type IntellioAssistantArtifactActionKind = z.infer<
  typeof IntellioAssistantArtifactActionKindSchema
>;

export const IntellioAssistantArtifactActionDescriptorSchema = z.object({
  id: z.string().trim().min(1),
  kind: IntellioAssistantArtifactActionKindSchema,
  label: z.string().trim().min(1),
  href: z.string().trim().min(1).nullable().optional(),
  sourceRef: z.string().trim().min(1).nullable().optional(),
  artifactType: z.string().trim().min(1).nullable().optional(),
  policyEventType: z.string().trim().min(1).nullable().optional(),
  disabledReason: z.string().trim().min(1).nullable().optional(),
  metadata: JsonObjectSchema.optional(),
}).strict();
export type IntellioAssistantArtifactActionDescriptor = z.infer<
  typeof IntellioAssistantArtifactActionDescriptorSchema
>;

export const IntellioAssistantTenantScopeStatusSchema = z.enum([
  "scoped",
  "legacy_placeholder",
]);
export type IntellioAssistantTenantScopeStatus = z.infer<
  typeof IntellioAssistantTenantScopeStatusSchema
>;

export const IntellioAssistantArtifactRefSchema = z.object({
  id: z.string(),
  artifactType: IntellioAssistantArtifactTypeSchema,
  label: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  storageKind: z.enum(["s3-signed-url"]),
  downloadUrl: z.string().url(),
  route: z.string().trim().min(1).nullable().optional(),
  routeFamily: z.string().trim().min(1).nullable().optional(),
  activeRouteContextRef: z.string().trim().min(1).nullable().optional(),
  routeRevision: z.string().trim().min(1).nullable().optional(),
  expiresAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  actions: z.array(IntellioAssistantArtifactActionDescriptorSchema).optional(),
  filterSummary: z.string().trim().min(1).nullable().optional(),
  selectionSummary: z.string().trim().min(1).nullable().optional(),
});
export type IntellioAssistantArtifactRef = z.infer<
  typeof IntellioAssistantArtifactRefSchema
>;

export const IntellioAssistantRealtimePrioritySchema = z.enum([
  "low",
  "normal",
  "high",
  "critical",
  "urgent",
]);
export type IntellioAssistantRealtimePriority = z.infer<
  typeof IntellioAssistantRealtimePrioritySchema
>;

export const IntellioAssistantSnoozePresetSchema = z.enum([
  "one_hour",
  "four_hours",
  "tomorrow_morning",
  "next_business_day",
  "one_week",
]);
export type IntellioAssistantSnoozePreset = z.infer<
  typeof IntellioAssistantSnoozePresetSchema
>;

export const IntellioAssistantRealtimeEventKindSchema = z.enum([
  "work.created",
  "work.updated",
  "work.dismissed",
  "work.snoozed",
  "work.completed",
  "notification.projected",
  "run.started",
  "run.progress",
  "run.token",
  "run.requires_confirmation",
  "run.completed",
  "run.failed",
]);
export type IntellioAssistantRealtimeEventKind = z.infer<
  typeof IntellioAssistantRealtimeEventKindSchema
>;

export const IntellioAssistantWorkItemSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorRole: z.string(),
  tenantId: z.string(),
  tenantScopeStatus: IntellioAssistantTenantScopeStatusSchema.optional(),
  state: IntellioAssistantWorkStateSchema,
  urgency: IntellioAssistantUrgencySchema,
  module: IntellioAssistantModuleSchema,
  route: z.string().trim().min(1),
  routeScore: z.number().int().optional(),
  title: z.string(),
  summary: z.string().nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  runId: z.string().nullable().optional(),
  workflowKey: z.string().nullable().optional(),
  workflowDefinitionId: z.string().nullable().optional(),
  workflowExecutionId: z.string().nullable().optional(),
  executionSnapshotHash: z.string().nullable().optional(),
  taskClass: IntellioAssistantTaskClassSchema.nullable().optional(),
  statusMessage: z.string().nullable().optional(),
  phaseKey: IntellioAssistantTaskPhaseSchema.nullable().optional(),
  phaseLabel: z.string().nullable().optional(),
  progressPercent: z.number().int().min(0).max(100).nullable().optional(),
  cancelable: z.boolean().optional(),
  launchSource: z.enum(["prompt", "action", "operator"]).nullable().optional(),
  resultSummary: z.string().nullable().optional(),
  artifactRefs: z.array(IntellioAssistantArtifactRefSchema).optional(),
  origin: IntellioAssistantConversationOriginRefSchema.nullable().optional(),
  traceId: z.string().nullable().optional(),
  notificationId: z.string().nullable().optional(),
  assistantPrompt: AssistantPromptPayloadSchema.nullable().optional(),
  nextVisibleAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  expiresAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  createdAtEpochMs: z.number().int().nonnegative(),
  updatedAtEpochMs: z.number().int().nonnegative(),
});
export type IntellioAssistantWorkItem = z.infer<
  typeof IntellioAssistantWorkItemSchema
>;

export const IntellioAssistantRunSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorRole: z.string(),
  actorGroupsJson: z.string().nullable().optional(),
  tenantId: z.string(),
  tenantScopeStatus: IntellioAssistantTenantScopeStatusSchema.optional(),
  state: IntellioAssistantRunStateSchema,
  module: IntellioAssistantModuleSchema,
  route: z.string().trim().min(1),
  question: z.string(),
  conversationHistoryJson: z.string().nullable().optional(),
  contextJson: z.string().nullable().optional(),
  runtimePreferencesJson: z.string().nullable().optional(),
  routingHintsJson: z.string().nullable().optional(),
  modelKey: z.string().nullable().optional(),
  workItemId: z.string().nullable().optional(),
  workflowKey: z.string().nullable().optional(),
  workflowDefinitionId: z.string().nullable().optional(),
  workflowExecutionId: z.string().nullable().optional(),
  executionSnapshotHash: z.string().nullable().optional(),
  origin: IntellioAssistantConversationOriginRefSchema.nullable().optional(),
  traceId: z.string().nullable().optional(),
  answer: z.string().nullable().optional(),
  responseJson: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  createdAtEpochMs: z.number().int().nonnegative(),
  startedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  completedAtEpochMs: z.number().int().nonnegative().nullable().optional(),
  updatedAtEpochMs: z.number().int().nonnegative(),
});
export type IntellioAssistantRun = z.infer<typeof IntellioAssistantRunSchema>;

export const IntellioAssistantRealtimeEventSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  tenantId: z.string(),
  tenantScopeStatus: IntellioAssistantTenantScopeStatusSchema.optional(),
  kind: IntellioAssistantRealtimeEventKindSchema,
  module: IntellioAssistantModuleSchema,
  route: z.string().trim().min(1),
  runId: z.string().nullable().optional(),
  workItemId: z.string().nullable().optional(),
  notificationId: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  priority: IntellioAssistantRealtimePrioritySchema.nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  traceId: z.string().nullable().optional(),
  sequence: z.number().int().nonnegative().nullable().optional(),
  payloadJson: z.string().nullable().optional(),
  occurredAt: UnixTimestamp,
});
export type IntellioAssistantRealtimeEvent = z.infer<
  typeof IntellioAssistantRealtimeEventSchema
>;

export const IntellioRewardPenaltyArtifactSchema = z
  .object({
    traceId: z.string().trim().min(1),
    artifactType: z.enum(["reward", "penalty", "neutral"]),
    reason: z.string().trim().min(1),
    confidence: z.number().min(0).max(1).optional(),
    actionScope: z
      .enum(["full", "draft_only", "navigation_only"])
      .default("navigation_only"),
    createdAt: z.string().trim().min(1),
  })
  .strict();
export type IntellioRewardPenaltyArtifact = z.infer<
  typeof IntellioRewardPenaltyArtifactSchema
>;

const parseJsonCandidate = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

export const parseIntellioRoutingHints = (
  value: unknown,
): IntellioRoutingHints => {
  const parsed = IntellioRoutingHintsSchema.safeParse(
    parseJsonCandidate(value),
  );
  return parsed.success ? parsed.data : {};
};

export const normalizeIntellioProactiveChannels = (
  value: unknown,
): IntellioProactiveChannel[] => {
  if (!Array.isArray(value)) {
    return ["in_app"];
  }

  const channels = value
    .map((entry) => IntellioProactiveChannelSchema.safeParse(entry))
    .filter((entry) => entry.success)
    .map((entry) => entry.data);

  return channels.length > 0 ? Array.from(new Set(channels)) : ["in_app"];
};
